import { useCallback } from 'react';

import { DEFAULT_CURRENCY } from '@/data/config/DEFAULTS';
import { usePersonInfo } from '@/data/Content/PersonInfo';
import { useSettings } from '@/data/Settings';
import { getExternalId } from '@/data/utils/externalId';
import { dFix } from '@/data/utils/floatingPoint';
import { PinterestClientPayload } from '@/pages/api/tracking/pinterest/pinterestAPIUtils';
import { useCurrentPhysicalStoreId } from 'presentation/events/hooks/useCurrentPhysicalStoreId';
import { useTrackingStoreLocation } from 'presentation/events/hooks/useTrackingStoreLocation';
import { usePinterestClickIdentifiers } from 'presentation/events/hooks/useClickIdentifiers';
import { triggerPinterestCAPIEvent } from 'presentation/events/services/pinterestService';
import { safeEncodeUrl } from 'presentation/events/utils/ga4Utils';
import {
	getItemPartNumber,
	getProductBrand,
	getProductPrice,
	getProductQuantity,
} from 'presentation/events/utils/productUtils';
import { SendAddToCartEventProps } from './types';

export const useTrackAddToCartPinterest = () => {
	const { settings } = useSettings();
	const { physicalStoreId } = useCurrentPhysicalStoreId();
	const storeLocation = useTrackingStoreLocation();
	const { personInfo } = usePersonInfo();
	const { epik } = usePinterestClickIdentifiers();

	return useCallback(
		(props: SendAddToCartEventProps) => {
			if (settings.userData?.ENABLE_PINTEREST_CALL !== 'true' || !physicalStoreId) return;

			const { eventId, storeId } = props;

			let contents: Array<{
				item_name: string;
				id: string;
				item_price: number;
				quantity: number;
				item_brand: string;
				item_category: string;
			}> = [];

			if (props.product) {
				// Single product

				// TODO: bundle logic needs to be implemented

				const { product, promotionDetails, categories } = props;

				const quantity = getProductQuantity({ quantity: props.quantity });

				const { price } = getProductPrice({
					product,
					quantity,
					physicalStoreId,
					promotionDetails,
				});

				contents = [
					{
						id: getItemPartNumber(product),
						item_name: product.name,
						item_price: price,
						item_category: categories.join(' > '),
						item_brand: getProductBrand(product),
						quantity,
					},
				];
			}

			if (props.items) {
				// Multiple products

				contents = props.items.map((item) => {
					const { product, promotionDetails, categories } = item;

					const quantity = getProductQuantity({ quantity: item.quantity });

					const { price } = getProductPrice({
						product,
						quantity,
						physicalStoreId,
						promotionDetails,
					});

					return {
						id: getItemPartNumber(product),
						item_name: product.name,
						item_price: price,
						item_category: categories.join(' > '),
						item_brand: getProductBrand(product),
						quantity,
					};
				});
			}

			const value = dFix(
				contents.reduce((sum, item) => sum + item.item_price * item.quantity, 0),
				2
			);
			const content_ids = contents.map((c) => c.id);

			const payload: PinterestClientPayload = {
				event_name: 'add_to_cart',
				event_id: eventId,
				storeId,
				event_source_url: safeEncodeUrl(window.location.href),
				epik,
				test: settings?.userData?.ENABLE_PINTEREST_TEST === 'true',
				custom_data: {
					contents: contents.map((content) => ({
						...content,
						item_price: content.item_price.toString(),
					})),
					num_items: contents.length,
					content_ids,
					content_type: 'product',
					currency: DEFAULT_CURRENCY,
					value: String(value),
					externalId: getExternalId(),
					email: personInfo?.email1,
					phone: personInfo?.phone1,
					firstName: personInfo?.firstName,
					lastName: personInfo?.lastName,
					city: storeLocation?.city,
					state: storeLocation?.state,
					zip: storeLocation?.zipCode,
				},
			};

			triggerPinterestCAPIEvent(payload);
		},
		[
			settings.userData?.ENABLE_PINTEREST_CALL,
			settings.userData?.ENABLE_PINTEREST_TEST,
			physicalStoreId,
			storeLocation,
			epik,
			personInfo?.email1,
			personInfo?.phone1,
			personInfo?.firstName,
			personInfo?.lastName,
		]
	);
};
